<template>
  <MainLayout>
    <ContentPageHeader functionName="買取査定管理" :pageName="'買取データ登録'">
    </ContentPageHeader>
    
    <div class="purchase-data-register-form">
      <div class="form-container">
        <div class="form-left">
          <FormDisplayField 
            label="カテゴリ"
            :value="selectedCategoryName"
          />

          <FormSelectSearchField 
            label="メーカー名" 
            id="manufacturer" 
            v-model="currentItem.manufacturer"
            :options="manufacturerOptions"
            size="medium"
          />
          
          <FormTextField 
            label="商品名" 
            id="productName" 
            v-model="currentItem.productName" 
            size="medium"
            maxlength="255"
          />
          
            <FormRadioField 
            label="ランク" 
            id="rank" 
            v-model="currentItem.rank" 
            :options="rankOptions"
            @change="onRankChange"
          />
          
          <FormSelectBoxField 
            label="付属品" 
            id="accessories" 
            v-model="currentItem.accessories" 
            :options="accessoriesOptions"
            size="small"
          />
          
          <FormNumberField 
            label="買取金額" 
            id="purchaseAmount" 
            v-model="currentItem.purchaseAmount" 
            size="small"
            isCurrency
            :isRedLabel="true"
          />
          
          <FormNumberField 
            label="販売金額" 
            id="salesAmount" 
            v-model="currentItem.salesAmount" 
            size="small"
            isCurrency
          />
          
          <FormNumberField 
            label="個数" 
            id="quantity" 
            v-model="currentItem.quantity" 
            size="small"
            disabled
          />
          
          <FormRadioField 
            label="加減点" 
            id="adjustment" 
            v-model="currentItem.adjustment" 
            :options="[
              { value: 'none', label: 'なし' },
              { value: 'plus', label: '＋' },
              { value: 'minus', label: 'ー' }
            ]"
          />
          
          <FormTextareaField 
            label="備考" 
            id="remarks" 
            v-model="currentItem.remarks" 
            :rows="4"
            maxlength="1000"
          />
          
          <div class="item-actions">
            <button class="btn btn-secondary" @click="updateItem">更新</button>
            <button class="btn btn-primary" @click="addItem">登録</button>
          </div>
        </div>
        
        <div class="form-right">
          <!-- カテゴリセクション -->
          <FormCollapsibleSection title="カテゴリ" :required="false">
            <FormSearchField 
              label="" 
              id="categorySearch" 
              v-model="categorySearchQuery" 
              placeholder="検索..."
              @search="searchCategories"
            />
            <FormHierarchicalRadioList
              id="categoryList"
              v-model.number="currentItem.category"
              :categories="categories"
              @change="onCategoryChange"
            />
          </FormCollapsibleSection>
        </div>
      </div>
      
      <div class="total-amount-display">
        <span class="total-amount-label">買取金額合計:</span>
        <span class="total-amount-value">¥{{ totalPurchaseAmount.toLocaleString() }}</span>
      </div>

      <div class="items-list-section">
        <h3><strong>買取商品一覧</strong></h3>
        <FormDataTable
          :items="purchaseItems"
          :columns="columns"
          :actions="actions"
          :showPagination="true"
          emptyMessage="商品がありません"
        >
          <!-- 不成立チェックボックスのカスタムレンダリング -->
          <template #isSatisfied="{ item }">
            <input type="checkbox" v-model="item.isSatisfied" />
          </template>
        </FormDataTable>
      </div>
    </div>
    
    <template #footer-buttons>
      <div class="footer-buttons-container">
        <button class="btn btn-primary" @click="registerAndPrint">印刷</button>
        <div style="padding: 0 10px;"></div>
        <button class="btn btn-success" @click="registerPOS">POS登録</button>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormDisplayField from '@/components/form/FormDisplayField.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormSelectSearchField from '@/components/form/FormSelectSearchField.vue'
import FormSelectBoxField from '@/components/form/FormSelectBoxField.vue'
import FormNumberField from '@/components/form/FormNumberField.vue'
import FormTextareaField from '@/components/form/FormTextareaField.vue'
import FormRadioField from '@/components/form/FormRadioField.vue'
import FormCollapsibleSection from '@/components/form/FormCollapsibleSection.vue'
import FormSearchField from '@/components/form/FormSearchField.vue'
import FormHierarchicalRadioList from '@/components/form/FormHierarchicalRadioList.vue'
import FormDataTable from '@/components/form/FormDataTable.vue'
import apiService from '@/services/service.js'
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'PurchaseDataRegisterView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormDisplayField,
    FormSelectSearchField,
    FormSelectBoxField,
    FormNumberField,
    FormTextareaField,
    FormRadioField,
    FormCollapsibleSection,
    FormSearchField,
    FormHierarchicalRadioList,
    FormDataTable
  },
  data() {
    return {
      purchaseData: {},
      assessmentId: null, // 買取査定ID
      currentItem: {
        manufacturer: '',
        productName: '',
        rank: 'A',
        accessories: '',
        purchaseAmount: null,
        salesAmount: null,
        quantity: 1,
        adjustment: 'none', // 新しい項目
        remarks: '',
        category: ''
      },
      categorySearchQuery: '',
      categories: [],
      purchaseItems: [],
      manufacturerOptions: [],
      rankOptions: [],
      ranks: [],
      accessoriesOptions: [],
      // テーブルカラム定義
      columns: [
        { key: 'manufacturer', label: 'メーカー名', width: '15%', formatter: (value) => this.getManufacturerLabel(value) },
        { key: 'productName', label: '商品名' , width: '20%'},
        { key: 'rank', label: 'ランク', width: '5%', formatter: (value) => this.getRankLabel(value) },
        { key: 'purchaseAmount', label: '買取金額', width: '10%', formatter: (value) => `¥${(Number(value) || 0).toLocaleString()}` },
        { key: 'remarks', label: '備考', width: '35%'  },
        { key: 'isSatisfied', label: '不成立', width: '5%' }
      ],
      // 操作ボタン定義
      actions: [
        {
          label: '',
          icon: 'fa-edit',
          buttonClass: 'btn-secondary',
          action: (item) => this.editItemByItem(item),
          condition: () => true // 常に有効
        },
        {
          label: '',
          icon: 'fa-trash',
          buttonClass: 'btn-danger',
          action: (item) => this.removeItemByItem(item),
          condition: () => true // 買取商品の削除のみ常に有効
        }
      ],
      storeOptions: [], // 店舗オプション
      commonPurchaseSlip: {}, // 買取伝票共通情報
    }
  },
  computed: {
    /**
     * 買取金額合計を計算する
     */
    totalPurchaseAmount() {
      return this.purchaseItems.reduce((total, item) => {
        return item.isSatisfied 
          ? total
          : Number(total) + Number(item.purchaseAmount);
      }, 0);
    },
    /**
     * デフォルトのメーカーを取得する
     */
    defaultManufacturer() {
      return this.manufacturerOptions && 0 < this.manufacturerOptions.length ? this.manufacturerOptions[0].value : '';
    },
    /**
     * デフォルトのランクを取得する
     */
    defaultRank() {
      return this.rankOptions && 0 < this.rankOptions.length ? this.rankOptions[0].value : '';
    },
    /**
     * デフォルトのカテゴリを取得する
     */
    defaultCategory() {
       return this.categories && 0 < this.categories.length ? this.categories[0].items[0].value : '';
    },
    /**
     * デフォルトの付属品オプションを取得する
     */
    defaultAccessory() {
      return this.accessoriesOptions && 0 < this.accessoriesOptions.length ? this.accessoriesOptions[0].items[0].value : '';
    },
    /**
     * 選択中のカテゴリ名を取得する
     */
    selectedCategoryName() {
      if (!this.currentItem.category) return '';
      const selectedCategory = this.getcategory(this.currentItem.category);
      return selectedCategory ? selectedCategory.category : '';
    }
  },
  watch: {
    categories: {
      handler() {
        this.updateRankOptions(this.defaultRank);
      },
      deep: true
    },
    categorySearchQuery: {
      handler() {
        this.searchCategories();
      },
      immediate: true
    }
  },
  methods: {
    /**
     * メーカー一覧を取得する
     */
    async loadMakers() {
      try {
        const response = await apiService.getMakerList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからメーカーオプションを設定
        this.manufacturerOptions = response.data.makers.map(maker => ({
          value: maker.id,
          label: maker.maker
        }));

      } catch (error) {
        console.error('メーカー一覧の取得に失敗しました:', error);
        alert('メーカー一覧の取得中にエラーが発生しました。');
      }
    },
    
    /**
     * ランク一覧を取得する
     */
    async loadRanks() {
      try {
        const response = await apiService.getRankList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからランクオプションを設定
        this.ranks = response.data.ranks.map(rank => ({
          value: rank.id,
          label: rank.rank
        }));

      } catch (error) {
        console.error('ランク一覧の取得に失敗しました:', error);
        alert('ランク一覧の取得中にエラーが発生しました。');
      }
    },

    /**
     * 付属品一覧を取得する
     */
    async loadAccessories() {
      try {
        const response = await apiService.getAccessoryList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからランクオプションを設定
        this.accessoriesOptions = response.data.accessories.map(data => ({
          value: data.id,
          label: data.accessory
        }));

      } catch (error) {
        console.error('付属品一覧の取得に失敗しました:', error);
        alert('付属品一覧の取得中にエラーが発生しました。');
      }
    },

    /**
     * カテゴリ一覧を取得する
     */
    async loadCategories() {
      try {
        const response = await apiService.getCategoryList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからカテゴリ一覧を設定
        this.originalCategories = JSON.parse(JSON.stringify(response.data.categories));
        this.setCategoryView(this.originalCategories);

        // デフォルトで最初のカテゴリを設定
        this.currentItem.category = Number(this.defaultCategory);
      } catch (error) {
        console.error('カテゴリ一覧の取得に失敗しました:', error);
        alert('カテゴリ一覧の取得中にエラーが発生しました。');
      }
    },

    /**
     * 店舗一覧を取得する
     */
     async loadStoreOptions() {
      try {
        const response = await apiService.getStoreList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
        
        this.storeOptions = response.data.stores.map(store => ({
          value: store.id,
          label: store.storeName,
          tag: store.tagMasterId
        }));
      } catch (error) {
        console.error('店舗情報の取得に失敗しました:', error);
        alert('店舗情報の取得中にエラーが発生しました。');
      }
    },

    /**
     * カテゴリを表示用に整形する
     */
    setCategoryView(mappedCategories) {
      // カテゴリをマッピングして表示用に整形
      this.categories = mappedCategories?.map(parent => ({
        title: parent.categoryType,
        items: parent.childCategories?.map(child => ({
          label: child.category,
          value: child.id,
          ranks: child.ranks
        }))
      }));
    },

    /**
     * カテゴリIDからカテゴリを取得する
     */
    getcategory(categoryId){
      // カテゴリIDに基づいてカテゴリを取得
      const selectCategory = this.originalCategories.flatMap(cat => cat.childCategories)
        .find(cat => Number(cat.id) === Number(categoryId));
      return selectCategory ? selectCategory : null;
    },

    /**
     * 選択されたカテゴリに基づいてランク一覧を更新する
     */
    updateRankOptions(rankId) {
      const categoryId = this.currentItem.category;
      if (!categoryId) {
        this.rankOptions = [];
        return;
      }

      // 選択されたカテゴリを検索
      const selectedCategory = this.getcategory(categoryId)

      // カテゴリが見つからない場合は空のランク一覧を設定
      if (!selectedCategory || !selectedCategory.ranks) {
        this.rankOptions = [];
        console.error('selectedCategory not found');
        return;
      }

      // 選択されたカテゴリのランク一覧を設定
      this.rankOptions = selectedCategory.ranks.map(rank => ({
        value: rank.id,
        label: rank.rank
      }));

      // ランクを設定
      this.currentItem.rank = rankId ? rankId : this.defaultRank
    },
    
    /**
     * メーカー名を取得する
     */
    getManufacturerLabel(value) {
      const option = this.manufacturerOptions.find(opt => String(opt.value) === String(value));
      return option ? option.label : '';
    },
    
    /**
     * ランク名を取得する
     */
    getRankLabel(value) {
      // rankoptionsはカテゴリによって更新されるのでranksを利用する
      const option = this.ranks.find(opt => String(opt.value) === String(value));
      return option ? option.label : '';
    },

    /**
     * 付属品名を取得する
     */
     getAccessoryLabel(value) {
      const option = this.accessoriesOptions.find(opt => String(opt.value) === String(value));
      return option ? option.label : '';
    },
    
    /**
     * カテゴリが変更されたときの処理
     */
    async onCategoryChange() {

      // ランク一覧を更新
      this.updateRankOptions(this.defaultRank);

      // 商品説明テンプレートを更新
      this.onRankChange(true);
    },

    /**
     * ランクが変更時にテンプレ更新
     */
    async onRankChange() {

      // 商品説明テンプレートを更新
      this.updateProductDescriptionTemplate();
    },
    
    /**
     * 商品説明テンプレートを更新する
     */
    async updateProductDescriptionTemplate() {
      const categoryId = this.currentItem.category;
      const rankId = this.currentItem.rank;
      
      if (categoryId && rankId) {
        showLoading();
        try {
          const response = await apiService.getProductDescriptionTemplate(parseInt(categoryId), parseInt(rankId));
          if (response && response.status === 200 && response.data) {
            // 備考欄に反映
            this.currentItem.remarks = response.data.templateStatement;
          }
        } catch (error) {
          console.error('商品説明テンプレートの取得に失敗しました:', error);
          alert('商品説明テンプレートの取得中にエラーが発生しました。');
        } finally {
          hideLoading();
        }
      }
    },
    
    /**
     * フォームをリセットする
     */
    resetItemForm() {
      this.currentItem = {
        manufacturer: '',
        productName: '',
        rank: this.defaultRank || '',
        accessories: '',
        purchaseAmount: null,
        salesAmount: null,
        quantity: 1,
        adjustment: 'none',
        remarks: '',
        category: this.defaultCategory || ''
      };
      this.updateProductDescriptionTemplate();
    },

    /**
     * 商品を追加する
     */
    addItem() {
      // 商品を追加
      // 新規登録の場合商品IDを生成
      const newId = 0 < this.purchaseItems.length
        ? Math.max(...this.purchaseItems.map(item => item.id || 0)) + 1 
        : 1;
      this.currentItem.id = newId;
      this.purchaseItems.push({...this.currentItem});
      
      // フォームをリセット
      this.resetItemForm();

      // 買取商品の登録
      const commonPurchaseSlipId = this.$route.params.id;
      if (!commonPurchaseSlipId) return;
      this.registerPurchaseItems(commonPurchaseSlipId);
    },

    /**
     * 商品を更新
     */
    updateItem(){
      const item = this.purchaseItems.find(i => i.id === this.currentItem.id);
      if (!item || this.currentItem.id === undefined) {
        // 商品が見つからない場合はエラーメッセージを表示
        alert('編集中の商品情報は登録されていません。');
        return;
      }
      // 商品情報を更新
      Object.assign(item, this.currentItem);

      // 買取商品の登録
      const commonPurchaseSlipId = this.$route.params.id;
      if (!commonPurchaseSlipId) return;
      this.registerPurchaseItems(commonPurchaseSlipId);
    },

    /**
     * 商品を編集する
     * @param item 
     */
    editItemByItem(item) {

      // 選択したアイテムを編集フォームにセット
      this.currentItem = { ...item };

      // カテゴリに基づいてランク一覧を更新
      this.updateRankOptions(item.rank);
    },
    
    /**
     * 商品を削除する
     * @param item 
     */
    removeItemByItem(item) {
      const index = this.purchaseItems.findIndex(i => i === item);
      if (index === -1) return;
      this.purchaseItems.splice(index, 1);

      // 買取商品の登録
      const commonPurchaseSlipId = this.$route.params.id;
      if (!commonPurchaseSlipId) return;
      this.registerPurchaseItems(commonPurchaseSlipId);
    },

    /**
     * カテゴリーを検索
     */
    searchCategories() {

      if (!this.originalCategories) return;
      if (!this.categorySearchQuery || this.categorySearchQuery.trim().length === 0) {
        this.setCategoryView(this.originalCategories);
        return;
      }

      const query = this.categorySearchQuery.toLowerCase();
      this.categories = this.originalCategories
        .map(parent => {
          const filteredItems = parent.childCategories
            .filter(child => child.category.toLowerCase().includes(query))
            .map(child => {
              return {
                label: child.category,
                value: Number(child.id),
                ranks: child.ranks
              };
            });
            
          return {
            title: parent.categoryType,
            items: filteredItems
          };
        })
        .filter(category => 0 < category.items.length);
    },

    /**
     * 買取伝票を登録して印刷する
     */
    async registerAndPrint() {
      try {
        showLoading();
        // 買取伝票共通IDを取得
        const commonPurchaseSlipId = this.$route.params.id;
        if (!commonPurchaseSlipId) {
            throw new Error('買取伝票情報が見つかりません。');
        }
        
        // 買取商品が登録されていない場合はエラー
        if (this.purchaseItems.length === 0 ) {
          alert('買取商品が登録されていません。');
          return;
        }

        // 買取商品の登録
        await this.registerPurchaseItems(commonPurchaseSlipId);

        // 伝票ファイルの作成
        const purchaseFileRes = await apiService.generatePurchaseSlips(commonPurchaseSlipId);
        if (!purchaseFileRes || purchaseFileRes.status !== 200 || !purchaseFileRes.data) {
          throw new Error('error status: ' + purchaseFileRes.status + ' ' + (purchaseFileRes.data ? purchaseFileRes.data.error : 'No data returned'));
        }
        
        // HTMLコンテンツを直接取得
        const htmlContent = purchaseFileRes.data;
        
        // 新しいウィンドウを開いてHTMLを表示
        const printWindow = await window.open('', 'purchase-slip-print');
        if (!printWindow) return;
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // 印刷を実行
        printWindow.onload = () => {
          printWindow.print();
        };

      } catch (error) {
        alert('買取伝票の印刷中にエラーが発生しました。');
        console.error(error);
      } finally {
        hideLoading();
      }
    },
    
    /**
     * POS登録処理
     */
     async registerPOS() {
      try {
        showLoading();

        // バリデーションチェック
        const isValid = this.validationCheck();
        if (!isValid) {
          return;
        }

        // ユーザに確認
        const confirmRegistration = confirm('POS登録を実行しますか？');
        if (!confirmRegistration) {
          return; // ユーザがキャンセルした場合は処理を中断
        }

        // 買取伝票共通IDを取得
        const commonPurchaseSlipId = this.$route.params.id;
        if (!commonPurchaseSlipId) {
            throw new Error('買取伝票情報が見つかりません。');
        }

        // 買取商品の登録
        await this.registerPurchaseItems(commonPurchaseSlipId);

        // POS登録APIを呼び出し
        const response = await apiService.registerProductInfoFromPurchaseSlip(commonPurchaseSlipId);
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        alert('POS登録が完了しました。');
      } catch (error) {
        alert('POS登録中にエラーが発生しました');
        console.error(error);
      } finally {
        hideLoading();
      }
    },

    /**
     * バリデーションチェック
     */
    validationCheck() {
      for (const item of this.purchaseItems) {
        if (!item.productName) {
          alert('商品名が未入力の商品があります。');
          return false;
        }
        if (!item.accessories) {
          alert('付属品が未入力の商品があります。');
          return false;
        }
        if (!item.purchaseAmount) {
          alert('買取金額が未入力の商品があります。');
          return false;
        }
        if (!item.salesAmount) {
          alert('販売金額が未入力の商品があります。');
          return false;
        }
        if (!item.category) {
          alert('カテゴリが未入力の商品があります。');
          return false;
        }
        if (item.productName && 64 < item.productName.length) {
          alert('商品名が65文字以上の商品があります。');
          return false;
        }
        if (item.remarks && 1000 < item.remarks.length) {
          alert('備考が1001文字以上の商品があります。');
          return false;
        }
      }
      return true;
    },

    /**
     * PurchaseRequestオブジェクトを構築する
     * @param {number} commonPurchaseSlipId 買取伝票共通ID
     * @returns {Object} PurchaseRequestオブジェクト
     */
    buildPurchaseRequest(commonPurchaseSlipId) {

      // purchaseItemsをPurchaseRequest形式に変換
      return {
        commonPurchaseSlip: {
          id: parseInt(commonPurchaseSlipId)
        },
        purchaseSlips: this.purchaseItems.map(item => ({
          id: null, // idは自動割り振りなので基本null
          purchaseSlipId: parseInt(commonPurchaseSlipId),
          makerId: parseInt(item.manufacturer) || null,
          productName: item.productName,
          rankId: parseInt(item.rank) || null,
          purchaseAmount: item.purchaseAmount,
          count: item.quantity,
          note: item.remarks,
          salesAmount: item.salesAmount,
          accessoryId: parseInt(item.accessories) || null,
          categoryId: parseInt(item.category),
          isPlus: item.adjustment === 'plus' ? 1 : (item.adjustment === 'minus' ? -1 : 0),
          isSatisfied: item.isSatisfied ? 0 : 1,
          unsatisfiedMemo: item.isSatisfied ? '不成立' : ''
        })),
      };
    },
    
    /**
     * 買取査定データ取得
     * @param {number} id 買取査定ID
     */
    async getPurchaseData(purchaseSlipId) {
      try {
        
        // 買取伝票情報を取得
        const response = await apiService.getPurchaseById(purchaseSlipId);
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
         
        // 買取伝票情報がない
        if (!response.data) return;
        if (!response.data.commonPurchaseSlip) return;
        if (!response.data.purchaseSlips || response.data.purchaseSlips.length === 0) return;

        // 買取伝票情報を設定
        this.commonPurchaseSlip = response.data.commonPurchaseSlip;
        this.purchaseItems = response.data.purchaseSlips.map(slip => {
          return {
            id: slip.id,
            manufacturer: slip.makerId ? parseInt(slip.makerId) : '',
            productName: slip.productName || '',
            rank: slip.rankId ? parseInt(slip.rankId) : '',
            accessories: slip.accessoryId ? parseInt(slip.accessoryId) : '',
            purchaseAmount: slip.purchaseAmount || 0,
            salesAmount: slip.salesAmount || 0,
            quantity: slip.count || 1,
            adjustment: slip.isPlus === 1 ? 'plus' : (slip.isPlus === -1 ? 'minus' : 'none'),
            remarks: slip.note || '',
            category: slip.categoryId ? parseInt(slip.categoryId) : '',
            isSatisfied: slip.isSatisfied === 0
          };
        });

      } catch (error) {
        console.error('買取査定データの取得中にエラーが発生しました:', error);
      }
    },

    /**
     * 買取商品を登録する
     * @param {number} commonPurchaseSlipId 買取伝票共通ID
     */
    async registerPurchaseItems(commonPurchaseSlipId) {

      // バリデーションチェック
      if (this.purchaseItems.length === 0) {
          throw new Error('買取商品が登録されていません。');
      }

      // 買取商品を登録
      const purchaseRequest = this.buildPurchaseRequest(commonPurchaseSlipId);
      const response = await apiService.registerPurchaseProduct(purchaseRequest);
      if (!response || response.status !== 200) {
          throw new Error('error status: ' + response.status + ' ' + response.data.message);
      }
    },

    async fetchData() {
      try {
        showLoading();
        
        // 複数リクエストを並列で実行
        await Promise.all([
          this.loadMakers(),
          this.loadRanks(),
          this.loadAccessories(),
          this.loadCategories(),
          this.loadStoreOptions(),
        ]);

        // 備考欄に商品説明テンプレートを設定
        this.updateProductDescriptionTemplate()

        // 買取査定IDがある場合はデータを取得
        if (!this.$route.params.id) return;
        this.getPurchaseData(this.$route.params.id);

      } catch (error) {
        console.error('データ取得エラー:', error);
        alert('データの取得中にエラーが発生しました: ' + error.message);
      } finally {
        hideLoading();
      }
    },
  },

 async mounted() {
    // URLパラメータのIDをチェック
    const id = this.$route.params.id;
    if (!id) {
      this.$router.push({ name: 'PurchaseHistory' });
      return;
    }
    
    try {
      await this.fetchData();
    } catch (error) {
      // データが存在しない場合は履歴画面に戻る
      console.error('買取データの取得に失敗しました:', error);
      this.$router.push({ name: 'PurchaseHistory' });
    }
  }
}
</script>

<style scoped>
.purchase-data-register-form {
  display: flex;
  flex-direction: column;
  background-color: var(--gray-light);
  gap: 20px;
}

.form-container {
  display: flex;
  background-color: var(--gray-light);
  gap: 0;
}

.form-section {
  background-color: var(--gray-light);
  padding: 0;
}

.form-left {
  width: 80%;
  background-color: var(--gray-light);
  padding-right: 0;
}

.form-right {
  width: 20%;
  background-color: var(--gray-light);
  padding-left: 0;
}

.form-divider {
  border-top: 1px solid #ddd;
  margin: 15px 0;
}

.radio-group {
  margin-bottom: 15px;
}

.radio-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.radio-options {
  display: flex;
  gap: 20px;
}

.radio-options label {
  font-weight: normal;
  display: flex;
  align-items: center;
  gap: 5px;
}

.item-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}

.items-list-section {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  margin: 5px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.table th {
  font-weight: bold;
  background-color: #f8f9fa;
}

.table-responsive {
  overflow-x: auto;
  padding: 0 15px;
}

.text-right {
  text-align: right;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.edit-btn {
  margin-right: 15px;
}

.total-amount-display {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 10px 0 15px 0;
  padding: 0 15px;
}

.total-amount-label {
  font-weight: bold;
  margin-right: 10px;
}

.total-amount-value {
  font-weight: bold;
  font-size: 1.2rem;
  color: #195983;
}

.footer-buttons-container {
  display: flex;
  justify-content: flex-end;
}
</style>
